import { Notification as customNotification } from '../../../components/ui/custom-notification';
import get from 'lodash/get';
import {
  put,
  takeLatest,
} from 'redux-saga/effects';
import axiosConfig from '../axiosConfig';
import { 
  GET_LIST_MATERIAL_CATEGORY, 
  GET_LIST_MATERIAL_CATEGORY_SUCCESS, 
  GET_LIST_MATERIAL_CATEGORY_FAILURE,
  GET_ALL_MATERIALS,
  GET_ALL_MATERIALS_SUCCESS,
  GET_ALL_MATERIALS_FAILURE,
  GENERATE_MATERIAL_REPORT,
  GET_MATERIAL_REPORT_VERSIONS,
  GENERATE_MATERIAL_REGISTRY_REPORT,
  GET_REGISTRY_REPORT_VERSIONS,
  GET_INSPECTION_PERSONNEL_DOCUMENTS,
  GET_REGISTRY_BOOK_VERSIONS,
  GET_MATERIAL_REGISTRY_REPORT_VERSIONS,
  GET_MATERIAL_TRACEABILITY_ALL_SUMMARY,
  GET_MATERIAL_TRACEABILITY_ALL_DATA
} from './types';
import { 
  getListMaterialCategorySuccess, 
  getListMaterialCategoryFailure,
  getAllMaterialsSuccess, 
  getAllMaterialsFailure,
  generateMaterialReportSuccess,
  generateMaterialReportFailure,
  getMaterialReportVersionsSuccess,
  getMaterialReportVersionsFailure,
  generateMaterialRegistryReportSuccess,
  generateMaterialRegistryReportFailure,
  getRegistryReportVersionsSuccess,
  getRegistryReportVersionsFailure,
  getInspectionPersonnelDocumentsSuccess,
  getInspectionPersonnelDocumentsFailure,
  getRegistryBookVersionsSuccess,
  getRegistryBookVersionsFailure,
  getMaterialRegistryReportVersionsSuccess,
  getMaterialRegistryReportVersionsFailure,
  getMaterialTraceabilityAllSummarySuccess,
  getMaterialTraceabilityAllSummaryFailure,
  getMaterialTraceabilityAllDataSuccess,
  getMaterialTraceabilityAllDataFailure
} from './action';

function* getListMaterialCategorySaga() {
  try {
    // Always fetch all material categories (tags) regardless of payload
    const url = `/tags/getAllTags`;

    const res = yield axiosConfig.get(url);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(getListMaterialCategoryFailure());
      return;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data', 'tags'], []);
      yield put(getListMaterialCategorySuccess({ responseData }));
    } else {
      yield put(getListMaterialCategoryFailure());
      customNotification.error('Something went wrong.', 'Failed to fetch material categories.');
    }
  } catch (error) {
    yield put(getListMaterialCategoryFailure());
    customNotification.destroy();
    customNotification.error('Failed to fetch material categories', error.message || error.error);
  }
}

function* getAllMaterialsSaga({ project_id, project_type_id, pipe_id }) {
  try {
    const url = `/materialTraceability/getMaterialTraceabilityData?project_id=${project_id}&project_type_id=${project_type_id}&pipe_id=${pipe_id}`;
    const res = yield axiosConfig.get(url);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(getAllMaterialsFailure());
      return;
    }
    
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});
      // Create cache key for storing materials by project_id, project_type_id, pipe_id
      const cacheKey = `${project_id}_${project_type_id}_${pipe_id}`;
      yield put(getAllMaterialsSuccess({ responseData, cacheKey }));
    } else {
      yield put(getAllMaterialsFailure());
      customNotification.error('Something went wrong.', 'Failed to fetch materials.');
    }
  } catch (error) {
    yield put(getAllMaterialsFailure());
    customNotification.destroy();
    customNotification.error('Failed to fetch materials', error.message || error.error);
  }
}

function* generateMaterialReportSaga({ payload }) {
  try {
    const url = `/download-proxy/reports/material-sheet-main-report`;
    //const url = `/reports/material-sheet-main-report`;
    const res = yield axiosConfig.post(url, payload);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(generateMaterialReportFailure('Unauthorized access'));
      return;
    }
    
    if (res.status === 200 || res.status === 201) {
      const responseData = get(res, ['data', 'data'], res.data);
      yield put(generateMaterialReportSuccess(responseData));
      customNotification.success('Report Generated', 'Material traceability report has been generated successfully.');
    } else {
      yield put(generateMaterialReportFailure('Failed to generate report'));
      customNotification.error('Something went wrong.', 'Failed to generate material report.');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to generate report';
    yield put(generateMaterialReportFailure(errorMessage));
    customNotification.destroy();
    customNotification.error('Failed to generate report', errorMessage);
  }
}

function* getMaterialReportVersionsSaga({ pipeId, reportType }) {
  try {
    const url = `/pipeDetails/getPipeDetailsByIdAndType/${pipeId}/${reportType}`;
    const res = yield axiosConfig.get(url);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(getMaterialReportVersionsFailure('Unauthorized access'));
      return;
    }
    
    if (res.status === 200) {
      // Extract documents from data.documentsData.documents
      const documents = get(res, ['data', 'data', 'documentsData', 'documents'], []);
      
      // Flatten the nested structure - extract file data from files[0] into each document
      const flattenedDocuments = documents.map((doc) => {
        const file = doc.files && doc.files.length > 0 ? doc.files[0] : {};
        return {
          docRefId: doc.docRefId,
          docId: file.docId,
          type: doc.type,
          fileUrl: file.fileUrl,
          fileName: file.fileName,
          s3Key: file.s3Key,
          versionNumber: file.details?.versionNumber || 1,
          version: file.details?.version || 'v1',
          uploadDate: file.uploadDate,
          uploadedBy: file.uploadedBy,
          usedInProduction: file.usedInProduction,
          details: file.details,
          approvalStatus: file.approvalStatus || file.approval_status
        };
      });
      
      yield put(getMaterialReportVersionsSuccess(flattenedDocuments));
    } else {
      yield put(getMaterialReportVersionsFailure('Failed to fetch report versions'));
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch report versions';
    yield put(getMaterialReportVersionsFailure(errorMessage));
  }
}

function* generateMaterialRegistryReportSaga({ payload }) {
  try {
    const url = `/download-proxy/reports/material-registry-report`;
    //const url = `/reports/material-registry-report`;
    const res = yield axiosConfig.post(url, payload);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(generateMaterialRegistryReportFailure('Unauthorized access'));
      return;
    }
    
    if (res.status === 200 || res.status === 201) {
      const responseData = get(res, ['data', 'data'], res.data);
      yield put(generateMaterialRegistryReportSuccess(responseData));
      customNotification.success('Report Generated', 'Material registry report has been generated successfully.');
    } else {
      yield put(generateMaterialRegistryReportFailure('Failed to generate report'));
      customNotification.error('Something went wrong.', 'Failed to generate material registry report.');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to generate report';
    yield put(generateMaterialRegistryReportFailure(errorMessage));
    customNotification.destroy();
    customNotification.error('Failed to generate report', errorMessage);
  }
}

function* getRegistryReportVersionsSaga({ projectId }) {
  try {
    // Fetch all registry documents for the project (no specification filter)
    const url = `/reports/registry-documents?projectId=${projectId}`;
    
    const res = yield axiosConfig.get(url);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(getRegistryReportVersionsFailure('Unauthorized access'));
      return;
    }
    
    if (res.status === 200) {
      // Response is grouped by report type: { EQUIPMENT_WELDING_REPORT: [...], EQUIPMENT_NDE_REPORT: [...] }
      const groupedDocuments = get(res, ['data', 'data'], {});
      
      // Process each report type group and format documents
      const formattedGroups = {};
      Object.keys(groupedDocuments).forEach((reportType) => {
        const docs = groupedDocuments[reportType] || [];
        formattedGroups[reportType] = docs.map((doc) => ({
          docId: doc.doc_id,
          projectId: doc.project_id,
          specification: doc.specification,
          docRef: doc.docRef,
          fileName: doc.file_name,
          fileUrl: doc.fileUrl,
          uploadDate: doc.upload_date,
          uploadedBy: doc.uploaded_by,
          details: doc.details,
          approvalStatus: doc.approval_status
        }));
      });
      
      yield put(getRegistryReportVersionsSuccess(formattedGroups));
    } else {
      yield put(getRegistryReportVersionsFailure('Failed to fetch report versions'));
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch report versions';
    yield put(getRegistryReportVersionsFailure(errorMessage));
  }
}

function* getInspectionPersonnelDocumentsSaga({ projectId }) {
  try {
    // Fetch QC Inspection Personnel documents from dedicated endpoint
    const url = `/reports/inspection-personnel-documents?projectId=${projectId}`;
    
    const res = yield axiosConfig.get(url);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(getInspectionPersonnelDocumentsFailure('Unauthorized access'));
      return;
    }
    
    if (res.status === 200) {
      // Response is grouped by report type: { QC_INSPECTION_PERSONNEL_REPORT: [...] }
      const groupedDocuments = get(res, ['data', 'data'], {});
      
      // Process each report type group and format documents
      const formattedGroups = {};
      Object.keys(groupedDocuments).forEach((reportType) => {
        const docs = groupedDocuments[reportType] || [];
        formattedGroups[reportType] = docs.map((doc) => ({
          docId: doc.doc_id,
          projectId: doc.project_id,
          specification: doc.specification,
          docRef: doc.docRef,
          fileName: doc.file_name,
          fileUrl: doc.fileUrl,
          uploadDate: doc.upload_date,
          uploadedBy: doc.uploaded_by,
          details: doc.details,
          approvalStatus: doc.approval_status
        }));
      });
      
      yield put(getInspectionPersonnelDocumentsSuccess(formattedGroups));
    } else {
      yield put(getInspectionPersonnelDocumentsFailure('Failed to fetch inspection personnel documents'));
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch inspection personnel documents';
    yield put(getInspectionPersonnelDocumentsFailure(errorMessage));
  }
}

function* getRegistryBookVersionsSaga({ projectId }) {
  try {
    // Fetch previously generated registry books
    const url = `/reports/registry-book-versions?projectId=${projectId}`;
    
    const res = yield axiosConfig.get(url);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(getRegistryBookVersionsFailure('Unauthorized access'));
      return;
    }
    
    if (res.status === 200) {
      // Response structure: { data: { versions: [...], total: N } }
      const versions = get(res, ['data', 'data', 'versions'], []);
      
      // Format documents (API already returns camelCase, just ensure consistency)
      const formattedDocuments = versions.map((doc) => ({
        docId: doc.docId,
        docRef: doc.docRef,
        fileName: doc.fileName,
        fileUrl: doc.fileUrl,
        uploadDate: doc.uploadDate,
        uploadedBy: doc.uploadedBy,
        details: doc.details,
        approvalStatus: doc.approvalStatus,
        createdAt: doc.createdAt
      }));
      
      yield put(getRegistryBookVersionsSuccess(formattedDocuments));
    } else {
      yield put(getRegistryBookVersionsFailure('Failed to fetch registry book versions'));
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch registry book versions';
    yield put(getRegistryBookVersionsFailure(errorMessage));
  }
}

function* getMaterialRegistryReportVersionsSaga({ projectId }) {
  try {
    const url = `/reports/material-registry-reports?projectId=${projectId}`;
    const res = yield axiosConfig.get(url);
    
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(getMaterialRegistryReportVersionsFailure('Unauthorized access'));
      return;
    }
    
    if (res.status === 200) {
      // Extract the data object which contains report types as keys
      // Response structure: { data: { BASE_PIPE_REPORT: [...], FORGE_REPORT: [...], ... } }
      const reportsByType = get(res, ['data', 'data'], {});
      
      // Normalize field names and structure for each report type
      const normalizedReportsByType = {};
      Object.keys(reportsByType).forEach((reportType) => {
        const documents = reportsByType[reportType] || [];
        normalizedReportsByType[reportType] = documents.map((doc) => {
          return {
            docId: doc.doc_id || doc.docId,
            docRef: doc.docRef || doc.doc_ref || 1,
            type: doc.specification || doc.type || reportType,
            fileUrl: doc.fileUrl || doc.file_url,
            fileName: doc.file_name || doc.fileName,
            s3Key: doc.s3_key || doc.s3Key,
            uploadDate: doc.upload_date || doc.uploadDate || doc.created_at,
            uploadedBy: doc.uploaded_by || doc.uploadedBy,
            approvalStatus: doc.approval_status || doc.approvalStatus,
            details: doc.details || {},
            // Keep original fields for backward compatibility
            ...doc
          };
          });
        });
        
        yield put(getMaterialRegistryReportVersionsSuccess(normalizedReportsByType));
      } else {
        yield put(getMaterialRegistryReportVersionsFailure('Failed to fetch material registry report versions'));
      }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch material registry report versions';
    yield put(getMaterialRegistryReportVersionsFailure(errorMessage));
  }
}

function* getMaterialTraceabilityAllSummarySaga({ payload = {} }) {
  try {
    const url = `/materialTraceability/getMaterialTraceabilityAllSummaryData`;
    const res = yield axiosConfig.post(url, payload);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(getMaterialTraceabilityAllSummaryFailure('Unauthorized access'));
      return;
    }

    if (res.status === 200 || res.status === 201) {
      const responseData = get(res, ['data', 'data'], get(res, 'data', {}));
      yield put(getMaterialTraceabilityAllSummarySuccess(responseData));
    } else {
      yield put(getMaterialTraceabilityAllSummaryFailure('Failed to fetch material traceability summary'));
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch material traceability summary';
    yield put(getMaterialTraceabilityAllSummaryFailure(errorMessage));
  }
}

function* getMaterialTraceabilityAllDataSaga({ payload }) {
  try {
    const url = `/materialTraceability/getMaterialTraceabilityAllData`;
    const res = yield axiosConfig.post(url, payload || {});

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      yield put(getMaterialTraceabilityAllDataFailure('Unauthorized access'));
      return;
    }

    if (res.status === 200 || res.status === 201) {
      const resData = get(res, 'data', {});
      // API returns: { data: { pipes: [], forge: [], gas: [], wireLot: [], coating: [], nde: [] }, total, limit, offset }
      const dataObj = get(resData, 'data', get(resData, 'materials', {}));
      const data = typeof dataObj === 'object' && dataObj !== null && !Array.isArray(dataObj)
        ? dataObj
        : {};
      const total =resData.data.total //get(resData, 'total', 0);
      yield put(getMaterialTraceabilityAllDataSuccess({ data, total }));
    } else {
      yield put(getMaterialTraceabilityAllDataFailure('Failed to fetch material traceability data'));
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch material traceability data';
    yield put(getMaterialTraceabilityAllDataFailure(errorMessage));
  }
}

  function* getIRNReportVersionsSaga({ projectId }) {
    try {
      const url = `/reports/inspection-release-note-registry-reports?projectId=${projectId}`;
      const res = yield axiosConfig.get(url);
      
      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        yield put(getMaterialRegistryReportVersionsFailure('Unauthorized access'));
        return;
      }
      
      if (res.status === 200) {
        // Extract the data object which contains report types as keys
        // Response structure: { data: { BASE_PIPE_REPORT: [...], FORGE_REPORT: [...], ... } }
        const reportsByType = get(res, ['data', 'data'], {});
        
        // Normalize field names and structure for each report type
        const normalizedReportsByType = {};
        Object.keys(reportsByType).forEach((reportType) => {
          const documents = reportsByType[reportType] || [];
          normalizedReportsByType[reportType] = documents.map((doc) => {
            return {
              docId: doc.doc_id || doc.docId,
              docRef: doc.docRef || doc.doc_ref || 1,
              type: doc.specification || doc.type || reportType,
              fileUrl: doc.fileUrl || doc.file_url,
              fileName: doc.file_name || doc.fileName,
              s3Key: doc.s3_key || doc.s3Key,
              uploadDate: doc.upload_date || doc.uploadDate || doc.created_at,
              uploadedBy: doc.uploaded_by || doc.uploadedBy,
              approvalStatus: doc.approval_status || doc.approvalStatus,
              details: doc.details || {},
              // Keep original fields for backward compatibility
              ...doc
            };
            });
          });
          
          yield put(getMaterialRegistryReportVersionsSuccess(normalizedReportsByType));
        } else {
          yield put(getMaterialRegistryReportVersionsFailure('Failed to fetch material registry report versions'));
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch material registry report versions';
        yield put(getMaterialRegistryReportVersionsFailure(errorMessage));
      }
    }

function* materialsDataWatcher() {
  yield takeLatest(GET_LIST_MATERIAL_CATEGORY, getListMaterialCategorySaga);
  yield takeLatest(GET_ALL_MATERIALS, getAllMaterialsSaga);
  yield takeLatest(GENERATE_MATERIAL_REPORT, generateMaterialReportSaga);
  yield takeLatest(GET_MATERIAL_REPORT_VERSIONS, getMaterialReportVersionsSaga);
  yield takeLatest(GENERATE_MATERIAL_REGISTRY_REPORT, generateMaterialRegistryReportSaga);
  yield takeLatest(GET_REGISTRY_REPORT_VERSIONS, getRegistryReportVersionsSaga);
  yield takeLatest(GET_INSPECTION_PERSONNEL_DOCUMENTS, getInspectionPersonnelDocumentsSaga);
  yield takeLatest(GET_REGISTRY_BOOK_VERSIONS, getRegistryBookVersionsSaga);
  yield takeLatest(GET_MATERIAL_REGISTRY_REPORT_VERSIONS, getMaterialRegistryReportVersionsSaga);
  yield takeLatest(GET_MATERIAL_TRACEABILITY_ALL_SUMMARY, getMaterialTraceabilityAllSummarySaga);
  yield takeLatest(GET_MATERIAL_TRACEABILITY_ALL_DATA, getMaterialTraceabilityAllDataSaga);
}

export default function* materialsDataSaga() {
  yield materialsDataWatcher();
}

